import * as React from 'react';
import ProtectedRoute from '../ProtectedRoute';
import Loader from 'components/Loader';
import ErrorBoundary from '../ErrorBoundary';
import { CustomerFacingError } from 'utils/ui/errors';
import { LoaderFunctionArgs, Outlet, redirect, RouteObject } from 'react-router';
import { IRoutingContext } from 'router/context';
import {
  clearSession,
  initSession,
  isSessionDeleted,
  loadPublicSession,
  resetSession,
  userSession,
} from 'state/session';
import { getLocale } from 'i18n/locale';
import { brandSchema, IMarket, marketSchema } from 'data/schema';
import { EErrorCode } from 'config/errors-codes';
import { ConfigurationTable } from 'containers/SelfService';
import DealerEntry from 'containers/DealerEntry';
import { SecureOutlet } from 'router/SecureOutlet';
import { tenantConfig } from 'utils/tenant';
import { user } from 'utils/auth/user';
import { isDealerGatePassed } from 'utils/auth/dealerGate';
import GoodbyeRoute from 'router/components/GoodbyeRoute';
import ScheduledOrDeletedRoute from 'router/components/ScheduledOrDeletedRoute';

const Home = React.lazy(() => import('containers/Home'));
const Legal = React.lazy(() => import('containers/LegalDocuments/Legal'));
const Imprint = React.lazy(() => import('containers/LegalDocuments/Imprint'));
const MarketDashboard = React.lazy(() => import('containers/SelfService/MarketDashboard'));

function contextLoader(): IRoutingContext {
  return { userType: 'dealer' };
}

async function loadSessionInContext(options: LoaderFunctionArgs) {
  const { params } = options;
  const { sessionId } = params;
  if (!sessionId) {
    return;
  }

  await loadPublicSession(sessionId);
}

export async function contextLoaderOffline(options: LoaderFunctionArgs): Promise<IRoutingContext> {
  // AVS-4405: until the dealer gate has passed nothing may load or write the session; SecureOutlet revalidates after it
  if (!isDealerGatePassed(options.params.brand ?? '', options.params.market ?? '')) {
    return contextLoader();
  }

  // gotta load session data so DealerEntry doesn't trip
  await loadSessionInContext(options);
  // disable 3d rendering and syncing for premature access to a session
  userSession().setValues({ offline: true, activeExperience: '2D', isShared: false });
  return contextLoader();
}

export async function dealerSessionLoader(options: LoaderFunctionArgs): Promise<Response | IRoutingContext> {
  const { params } = options;
  const { sessionId } = params;

  // AVS-4405: no session is loaded, created or broadcast before the dealer is chosen. SecureOutlet
  // revalidates the loaders once the gate has passed, which runs this again with the gate open.
  if (!isDealerGatePassed(params.brand ?? '', params.market ?? '')) {
    return contextLoader();
  }

  await initSession(sessionId);

  const { brand, market, scheduledStartTime } = userSession().get();

  // AVS-4080: A scheduling-deleted session would otherwise fall through
  // to the home dashboard and silently spawn a brand-new session. Funnel
  // dealers through the /scheduled route so ScheduledOrDeletedRoute can
  // render the explicit "Session unavailable" dialog with a button to
  // start a new session (preserving the previously selected vehicle).
  if (isSessionDeleted(userSession().get())) {
    throw redirect(`/${brand}/${market}/${sessionId}/scheduled`);
  }

  if (userSession().sessionEnded && !userSession().presentationEndedWithoutRating) {
    clearSession();
    resetSession();
    throw redirect(`/${brand}/${market}/`);
  }

  // Check if session is scheduled
  if (scheduledStartTime) {
    const now = new Date();
    const scheduledTime = new Date(scheduledStartTime);

    // Get time window configuration from tenant config
    const { startWindowBeforeMinutes: startWindowBefore, startWindowAfterMinutes: startWindowAfter } =
      tenantConfig().getScheduler();

    // Calculate the time window boundaries
    const earliestStartTime = new Date(scheduledTime.getTime() - startWindowBefore * 60 * 1000);
    const latestStartTime = new Date(scheduledTime.getTime() + startWindowAfter * 60 * 1000);

    // If trying to access before the time window, redirect to scheduled page
    if (now < earliestStartTime) {
      throw redirect(`/${brand}/${market}/${sessionId}/scheduled`);
    }

    // If trying to access after the time window has expired, redirect to goodbye page
    if (now > latestStartTime) {
      throw redirect(`/${brand}/${market}/${sessionId}/goodbye`);
    }
  }

  return contextLoader();
}

const localizedRoutes: RouteObject[] = [
  {
    path: '/:brand/:market/legal/privacy-policy',
    element: (
      <ProtectedRoute>
        <React.Suspense fallback={<Loader />}>
          <Legal />
        </React.Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/:brand/:market/legal/imprint',
    element: (
      <ProtectedRoute>
        <React.Suspense fallback={<Loader />}>
          <Imprint />
        </React.Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/:brand/:market/',
    element: (
      <ProtectedRoute>
        <React.Suspense fallback={<Loader />}>
          <DealerEntry />
        </React.Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        // Home is rendered for both / and /:sessionId, but only mounted once
        path: '',
        element: <Home />, // always mounted
        children: [
          {
            path: ':sessionId',
            element: <Outlet />,
            loader: dealerSessionLoader,
            children: [
              {
                path: '',
                element: <React.Fragment />,
                loader: contextLoader,
              },
            ],
          },
          {
            path: ':sessionId/goodbye',
            element: <GoodbyeRoute />,
            loader: contextLoaderOffline,
          },
          {
            path: ':sessionId/scheduled',
            element: <ScheduledOrDeletedRoute />,
            loader: contextLoaderOffline,
          },
        ],
      },
    ],
  },
].map((route) => ({
  ...route,
  errorElement: <ErrorBoundary />,
  loader: contextLoader,
  ...route,
}));

export const dealerRoutes: RouteObject[] = [
  {
    path: '/',
    element: <Outlet />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: '/',
        element: <React.Fragment />,
        loader: () => {
          const brand = user.brand;
          const market = user.market.iso3;

          if (!brand || !market) {
            throw new CustomerFacingError(EErrorCode.USER_TOKENS_MISSING);
          }
          return redirect(`/${brand}/${market}/`);
        },
      },
      {
        path: '/super-sheep/self-service',
        element: (
          <ProtectedRoute key="self-service">
            <Outlet />
          </ProtectedRoute>
        ),
        loader: async () => {
          const { bootstrapPortalAuth } = await import('utils/auth/portal-iframe');
          await bootstrapPortalAuth();
          return null;
        },
        children: [
          {
            index: true,
            element: <ConfigurationTable />,
          },
          {
            path: ':brand/:market/:tenant',
            element: (
              <React.Suspense fallback={<Loader />}>
                <MarketDashboard />
              </React.Suspense>
            ),
          },
        ],
      },
      {
        path: '/:brand/:market',
        element: <SecureOutlet />,
        children: localizedRoutes,
        loader: async ({ params }) => {
          const { brand, market } = params;
          if (!brandSchema.safeParse(brand).success) {
            throw new CustomerFacingError(EErrorCode.INVALID_BRAND);
          }

          if (!marketSchema.safeParse(market).success) {
            throw new CustomerFacingError(EErrorCode.INVALID_MARKET);
          }

          // AVS-4405: brand and market are written by the dealer gate (SecureOutlet) once a dealer is chosen,
          // because that write is what makes tenant config load. Only the locale is known and safe before.
          userSession().setValues({ locale: getLocale(market as IMarket) });

          return true;
        },
      },
    ],
  },
];
